<template>
  <LayoutContainer class="bg-gray-100">
    <Layout column>
      <Banner :alert="notification" @close="onBannerClose" />
      <Flex column group-select>
        <Flex items-center text-2xl>
          {{ $t('Questionnaire.FormTitle') }}
        </Flex>
        <Flex items-center v-if="!loading">
          <span class="mr-1">{{ $t('groupBy') }}:</span>
          <Select
            v-if="showGroupingSelect"
            required
            :items="[
              { data: { value: 'location', label: $t('Location') } },
              { data: { value: 'timePeriod', label: $t('TimePeriod') } },
              { data: { value: 'sustainabilityMetricGroup', label: $t('MetricGroup') } },
              { data: { value: 'sustainabilityMetric', label: $t('Metric') } },
            ]"
            :value="groupBy"
            @update:value="groupingUpdated"
          />
          <Checkbox
            class="pl-8"
            :label="$t('showCompletedData')"
            :value="showCompletedData"
            :checked="showCompletedData"
            @click="toggleCompletedData($event)"
          />
          <Checkbox
            class="pl-8"
            :label="$t('showCalculatedMetrics')"
            :value="showCalculatedMetrics"
            :checked="showCalculatedMetrics"
            @click="toggleShowCalculatedMetrics($event)"
          />
        </Flex>
      </Flex>
      <Layout>
        <Loading :active="loading" :canCancel="false" loader="bars" />
        <Flex v-show="!loading" outer-panel>
          <GroupProgress v-if="dataExists" />
        </Flex>
        <Layout>
          <Form v-if="!loading && formValues" :lazy="false" :values="formValues">
            <Flex v-if="!dataExists" no-data-info>
              {{ $t('noData') }}
            </Flex>
            <DialogGroup
              v-for="(group, index) in groups"
              :key="group"
              :title="group"
              :items="tilesByGroup[group]"
              :alwaysVisible="index === 0"
              :groupBy="groupBy"
            ></DialogGroup>
          </Form>
        </Layout>
        <Flex v-show="!loading" outer-panel column>
          <Flex column v-if="campaigns.length > 0" campaigns-container>
            <Flex text-lg mb-2>{{ $t('campaigns') }}</Flex>
            <Checkbox
              v-for="campaign in campaigns"
              :key="campaign.id"
              :id="campaign.id"
              :label="campaign.name"
              :value="campaign.selected"
              :checked="campaign.selected"
              @click="toggleCampaign($event, campaign.id)"
            />
          </Flex>
          <Flex grow px-8 class="vld-parent">
            <Loading
              :active="isSaveAllInProgress"
              :canCancel="false"
              loader="spinner"
              :is-full-page="false"
              :color="primaryColor"
            />
            <OverallProgress
              v-if="!loading && !!progressValue && !isSaveAllInProgress"
              :percentage="progressValue"
              :title="progressTitle"
              :color="primaryColor"
            />
          </Flex>
          <Flex pl-8 pb-8>
            <Button
              v-if="dataExists"
              :disabled="!isSaveAllEnabled"
              :outlined="true"
              prepend-icon="bwise-full/save"
              v-tooltip.top="$t('Button.Save')"
              class="save-all-button"
              @click="saveAllEvent"
            >
              {{ $t('saveAllButton') }}
            </Button>
          </Flex>
        </Flex>
      </Layout>
    </Layout>
  </LayoutContainer>
</template>

<script>
import Form from '@wizer/core/components/form/Form'
import Flex from '@wizer/core/components/flex/Flex'
import Layout from '@wizer/core/components/layout/Layout'
import LayoutContainer from '@wizer/core/components/layout/LayoutContainer'
import Button from '@wizer/core/components/button/Button'
import Select from '@wizer/core/components/select/Select'
import Banner from '@wizer/core/components/banner/Banner'
import Checkbox from '@wizer/core/components/checkbox/Checkbox'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapGetters, mapState } from 'vuex'
import { pull, sortBy } from 'lodash'
import events from '@wizer/core/events'
import querystring from 'querystring'
import DialogGroup from '@/components/sustainability/DialogGroup'
import GroupProgress from '@/components/sustainability/GroupProgress'
import SustainabilityDataService from '@/services/SustainabilityDataService'
import UsingColorPalette from '@/mixins/UsingColorPalette'
import OverallProgress from '@/components/sustainability/OverallProgress'

export default {
  name: 'SustainabilityDataEntry.vue',
  mixins: [UsingColorPalette],
  components: {
    DialogGroup,
    GroupProgress,
    OverallProgress,
    Form,
    Flex,
    Layout,
    LayoutContainer,
    Button,
    Select,
    Banner,
    Checkbox,
    Loading,
  },
  props: {
    preSelectedLocation: String,
    preSelectedItem: String,
    checkShowCompletedData: String,
    checkShowCalculatedMetrics: String,
    groupByValue: String,
    excludeCampaignsIds: Array,
  },
  data() {
    return {
      excludeCampaigns: [],
      tilesByGroup: null,
      formValues: null,
      groupBy: this.groupByValue ? this.groupByValue : 'location',
      showCompletedData: false,
      showCalculatedMetrics: false,
      loading: true,
      showGroupingSelect: true,
      isSaveAllInProgress: false,
      groupByMap: new Map(),
    }
  },
  async mounted() {
    document.title = this.$t('Questionnaire.FormTitle')
    if (this.checkShowCompletedData) {
      this.showCompletedData = true
    }
    if (this.checkShowCalculatedMetrics) {
      this.showCalculatedMetrics = true
    }
    this.excludeCampaignsIds
      .filter((excludedCampaign) => excludedCampaign)
      .forEach((excludedCampaign) => this.excludeCampaigns.push(excludedCampaign))

    await this.populateValues()
    if (this.preSelectedLocation) {
      setTimeout(() => {
        events.publish('scroll-to-group', this.groupByMap[this.preSelectedLocation])
        if (this.preSelectedItem) {
          setTimeout(() => {
            events.publish('scroll-to-item', this.preSelectedItem)
          }, 500)
        }
      }, 500)
    }
  },
  methods: {
    saveAllEvent() {
      this.resetSaveQueue()
      events.publish('save-all')
    },
    async populateValues() {
      const data = (
        await SustainabilityDataService.getQuestionnaireData(
          this.groupBy,
          this.excludeCampaigns,
          this.showCompletedData,
          this.showCalculatedMetrics,
        )
      ).data
      const openResults = data.openResults
      this.groupByMap = data.progress.groupIdValues
      await this.setCurrentProgress(data.progress)
      this.tilesByGroup = openResults.items
      const campaigns = Object.entries(openResults.campaigns).map((entry) => ({
        id: entry[0],
        name: entry[1],
      }))
      const sortedGroups = this.tilesByGroup ? Object.keys(this.tilesByGroup).sort() : []
      this.formValues = this.getFormValues(openResults.items)
      await this.setQuestionnaireData({
        campaigns: sortBy(campaigns, 'name'),
        groups: sortedGroups,
        formValues: this.formValues,
        unitsOfMeasure: openResults.units,
      })
      this.campaigns.forEach((campaign) => {
        campaign.selected = !this.excludeCampaigns.includes(campaign.id)
      })
      this.loading = false
    },
    getFormValues(groups) {
      const formValues = {}
      const allItems = [].concat(...Object.values(groups))
      allItems.forEach((item) => {
        formValues[item.id] = item.inputValue
      })
      return formValues
    },
    groupingUpdated(newGroup) {
      if (this.isSaveAllEnabled) {
        // events.confirm has no handling for when user doesn't confirm
        // so revert the selection just made and if the reloadData
        // gets indeed called it will update it again,
        // we also need to hide and show the control again to refresh it
        this.showGroupingSelect = false
        const currentGroupBy = this.groupBy
        this.$nextTick(() => {
          this.groupBy = currentGroupBy
          this.showGroupingSelect = true
        })
      }
      this.confirmAndReload(() => {
        this.groupBy = newGroup
      })
    },
    reloadData(updateBeforeReload) {
      updateBeforeReload()
      const newLocation = `${this.getBaseUrl()}?${querystring.stringify({
        groupByValue: this.groupBy,
        checkShowCompletedData: this.showCompletedData ? 'true' : '',
        checkShowCalculatedMetrics: this.showCalculatedMetrics ? 'true' : '',
        excludeCampaignsIds: this.excludeCampaigns,
      })}`
      window.location.href = newLocation
      window.location.reload()
    },
    getBaseUrl() {
      return window.location.href.substring(
        0,
        window.location.href.indexOf(this.$route.path) + this.$route.path.length,
      )
    },
    confirmAndReload(updateBeforeReload) {
      if (this.isSaveAllEnabled) {
        events.confirm(() => this.reloadData(updateBeforeReload), this.$t('proceedAnyway'))
      } else {
        this.reloadData(updateBeforeReload)
      }
    },
    toggleCompletedData(event) {
      event.preventDefault()
      const checked = event.target.checked
      this.confirmAndReload(() => {
        this.showCompletedData = checked
      })
    },
    toggleShowCalculatedMetrics(event) {
      event.preventDefault()
      const checked = event.target.checked
      this.confirmAndReload(() => {
        this.showCalculatedMetrics = checked
      })
    },
    toggleCampaign(event, campaignId) {
      event.preventDefault()
      const includeCampaign = () => {
        pull(this.excludeCampaigns, campaignId)
      }
      const excludeCampaign = () => {
        this.excludeCampaigns.push(campaignId)
      }
      this.confirmAndReload(event.target.checked ? includeCampaign : excludeCampaign)
    },
    onBannerClose() {
      this.removeNotification(this.notifications.slice(-1)[0])
    },
    ...mapActions({
      removeNotification: 'Notification/remove',
      addNotification: 'Notification/add',
      resetSaveData: 'SustainabilityData/resetSaveData',
      resetSaveQueue: 'SustainabilityData/resetSaveQueue',
      setQuestionnaireData: 'SustainabilityData/setQuestionnaireData',
      setCurrentProgress: 'SustainabilityData/setCurrentProgress',
    }),
  },
  computed: {
    notification() {
      if (!this.notifications.length) return null
      return this.notifications.slice(-1)[0]
    },
    progressValue() {
      return this.overallProgress?.progress
    },
    progressTitle() {
      if (this.overallProgress) {
        return `${this.$t('progress')}: ${this.overallProgress.completed} / ${
          this.overallProgress.completed + this.overallProgress.outstanding
        }`
      }
      return ''
    },
    ...mapGetters('SustainabilityData', [
      'campaigns',
      'groups',
      'isSaveAllComplete',
      'overallProgress',
      'areAllSavesCollected',
      'getOrderedSaveQueue',
    ]),
    ...mapState({
      notifications: (state) => state.Notification.notifications,
      numberOfFailedSaves: (state) => state.SustainabilityData.numberOfFailedSaves,
      numberOfSaves: (state) => state.SustainabilityData.numberOfSaves,
      modifiedRecords: (state) => state.SustainabilityData.modifiedRecords,
      failedContextValidations: (state) => state.SustainabilityData.failedContextValidations,
      savesToExecuteInSequence: (state) => state.SustainabilityData.savesToExecuteInSequence,
    }),
    isSaveAllEnabled() {
      return !!this.modifiedRecords.length || this.failedContextValidations.size
    },
    dataExists() {
      return !this.loading && this.groups?.length > 0
    },
  },
  watch: {
    /* eslint-disable no-await-in-loop */
    async areAllSavesCollected() {
      if (this.areAllSavesCollected) {
        this.isSaveAllInProgress = true
        for (let i = 0; i < this.getOrderedSaveQueue.length; i++) {
          await this.getOrderedSaveQueue[i].saveToBeExecuted()
        }
        await this.resetSaveQueue()
        this.isSaveAllInProgress = false
      }
    },
    async isSaveAllComplete() {
      if (this.isSaveAllComplete && this.numberOfSaves > 0) {
        const notification = {
          type: this.numberOfFailedSaves ? 'warning' : 'success',
          message: this.numberOfFailedSaves ? this.$t('saveFailed') : this.$t('saveSuccessful'),
          duration: 5000,
        }
        await this.addNotification(notification)
        await this.resetSaveData()
      }
    },
  },
}
</script>

<style scoped lang="postcss">
.group-select {
  @apply items-center justify-center p-6;
}
.outer-panel {
  @apply w-1/4 border border-gray-300;
}
.no-data-info {
  @apply w-full justify-center bg-white text-gray-600 border border-gray-300 mt-4 p-5;
}
.save-all-button {
  @apply bg-white;
}
.campaigns-container {
  @apply p-8 overflow-x-hidden overflow-y-scroll;
}
</style>
